import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import type { Vendor, VendorStatus, PaymentTerms, PreferredPaymentMethod, ContactMethod } from '@/types/vendor';

interface VendorFormProps {
  onSubmit: (vendor: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>) => void;
  vendor?: Vendor | null;
  onCancel: () => void;
}

const PAYMENT_TERMS: PaymentTerms[] = ['immediate', 'net30', 'net60', 'net90'];
const PAYMENT_METHODS: PreferredPaymentMethod[] = ['ach', 'check', 'credit', 'wire'];
const CONTACT_METHODS: ContactMethod[] = ['phone', 'email', 'in-person'];
const VENDOR_STATUSES: VendorStatus[] = ['active', 'inactive', 'blacklisted'];

export function VendorForm({ onSubmit, onCancel, vendor }: VendorFormProps) {
  const [formData, setFormData] = useState({
    name: vendor?.name || '',
    contactName: vendor?.contactName || '',
    address: {
      street: vendor?.address.street || '',
      city: vendor?.address.city || '',
      state: vendor?.address.state || '',
      zipCode: vendor?.address.zipCode || '',
      country: vendor?.address.country || 'USA',
    },
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    website: vendor?.website || '',
    
    tin: vendor?.tin || '',
    ein: vendor?.ein || '',
    businessRegistrationNumber: vendor?.businessRegistrationNumber || '',
    industryType: vendor?.industryType || '',
    
    paymentTerms: vendor?.paymentTerms || 'net30' as PaymentTerms,
    preferredPaymentMethod: vendor?.preferredPaymentMethod || 'ach' as PreferredPaymentMethod,
    bankingInformation: {
      bankName: vendor?.bankingInformation.bankName || '',
      accountNumber: vendor?.bankingInformation.accountNumber || '',
      routingNumber: vendor?.bankingInformation.routingNumber || '',
    },
    invoiceEmail: vendor?.invoiceEmail || '',
    
    servicesProvided: Array.isArray(vendor?.servicesProvided) ? vendor.servicesProvided.join(', ') : '',
    serviceRegions: Array.isArray(vendor?.serviceRegions) ? vendor.serviceRegions.join(', ') : '',
    rating: vendor?.rating || 5,
    complianceStatus: vendor?.complianceStatus ?? true,
    insurance: {
      provider: vendor?.insurance.provider || '',
      policyNumber: vendor?.insurance.policyNumber || '',
      coverageAmount: vendor?.insurance.coverageAmount || 0,
      expirationDate: vendor?.insurance.expirationDate || '',
    },
    
    preferredContactMethod: vendor?.preferredContactMethod || 'email' as ContactMethod,
    businessHours: vendor?.businessHours || '',
    emergencyContact: {
      name: vendor?.emergencyContact.name || '',
      phone: vendor?.emergencyContact.phone || '',
      email: vendor?.emergencyContact.email || '',
    },
    
    contract: {
      startDate: vendor?.contract.startDate || '',
      endDate: vendor?.contract.endDate || '',
      renewalTerms: vendor?.contract.renewalTerms || '',
      terminationClause: vendor?.contract.terminationClause || '',
    },
    
    notes: vendor?.notes || '',
    status: vendor?.status || 'active' as VendorStatus,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      certifications: [],
      attachments: [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col max-h-[80vh]">
      <div className="flex items-center justify-between p-6 border-b">
        <div className="flex items-center space-x-2">
          <Building2 className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-800">{vendor ? 'Edit Vendor' : 'Add New Vendor'}</h2>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>

      <div className="overflow-y-auto p-6 space-y-6" style={{ maxHeight: 'calc(80vh - 140px)' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Vendor Name</label>
          <input
            type="text"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Contact Name</label>
          <input
            type="text"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.contactName}
            onChange={e => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.email}
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Phone</label>
          <input
            type="tel"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.phone}
            onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">TIN</label>
          <input
            type="text"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.tin}
            onChange={e => setFormData(prev => ({ ...prev, tin: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Payment Terms</label>
          <select
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.paymentTerms}
            onChange={e => setFormData(prev => ({ ...prev, paymentTerms: e.target.value as PaymentTerms }))}
          >
            {PAYMENT_TERMS.map(term => (
              <option key={term} value={term}>
                {term.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Preferred Payment Method</label>
          <select
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.preferredPaymentMethod}
            onChange={e => setFormData(prev => ({ 
              ...prev, 
              preferredPaymentMethod: e.target.value as PreferredPaymentMethod 
            }))}
          >
            {PAYMENT_METHODS.map(method => (
              <option key={method} value={method}>
                {method.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={formData.status}
            onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as VendorStatus }))}
          >
            {VENDOR_STATUSES.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Services Provided</label>
          <textarea
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            rows={3}
            value={formData.servicesProvided}
            onChange={e => setFormData(prev => ({ ...prev, servicesProvided: e.target.value }))}
            placeholder="List services separated by commas"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            rows={3}
            value={formData.notes}
            onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          />
        </div>
      </div>
      </div>

      <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 sticky bottom-0">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {vendor ? 'Update Vendor' : 'Add Vendor'}
        </button>
      </div>
    </form>
  );
}